import { useState } from 'react'
import {
  Card,
  Col,
  Container,
  Alert,
  Row,
  Breadcrumb,
  Button,
  Form,
} from 'react-bootstrap'
import CopyToClipboardButton from 'components/copy-to-clipboard-button'
import StripeProductTable from 'components/stripe-products-table'
import {
  HubspotDealLink,
  StripeSelect,
} from 'components/stripe-link-builder-components'
import { useCreateStripeCheckoutLink } from 'hooks/use-admin-stripe-checkout-links'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { LineItem } from 'interfaces/line-item'
import { User } from 'interfaces/user'
import { RootState } from 'index'
import { StripeProduct } from 'interfaces/stripe-product'
import AddProductModal from 'components/add-product-modal'
import { Price } from 'interfaces/price'
import { useAdminStripeAvailableCoupons } from 'hooks/use-admin-stripe'
import SelectCouponModal from 'components/select-coupon-modal'
import { WEB_APP_URL } from 'constants/env'

const NonSubscriptionLinkBuilderPage = () => {
  const currentUser = useSelector<RootState, User | null>(
    (state) => state.user.currentUser
  )
  const [couponId, setCouponId] = useState<string | number | undefined>('')
  const [saleRepId, setSaleRepId] = useState<string | number | undefined>(
    currentUser?.id
  )
  const [addedProductsList, setAddedProductsList] = useState<
    Partial<StripeProduct>[]
  >([])
  const [savedLineItems, setSavedLineItems] = useState<Partial<LineItem>[]>([])
  const [checkoutLink, setCheckoutLink] = useState<string | undefined>(
    undefined
  )
  const [hubspotDealLink, setHubspotDealLink] = useState<string>('')
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [showCouponModal, setShowCouponModal] = useState(false)
  const [collectShippingAddress, setCollectShippingAddress] = useState(true)
  const [addedPrices, setAddedPrices] = useState<Price[]>([])
  const { mutate: stripeCheckoutLink, isLoading: stripeCheckoutLinkLoading } =
    useCreateStripeCheckoutLink()

  const { data: internalAdmins, isFetching: isInternalAdminFetching } =
    useGetInternalAdmins()

  const { data: availableCoupons } = useAdminStripeAvailableCoupons()

  const handleAddProducts = (
    productId: number | string | undefined,
    productPriceId: number | string | undefined,
    productQuantity: number,
    productPrices: Price[],
    availableProducts: StripeProduct[],
    allowQuantityAdjusting: boolean
  ) => {
    if (productQuantity < 1) {
      window.alert('Quantity must be greater than 0')
      return
    }
    if (productId && productPriceId) {
      const selectedPrice = productPrices?.find(
        (price) => price.id === productPriceId
      )

      const selectedProduct = availableProducts?.find(
        (product) => product.id === productId
      )

      if (selectedPrice) {
        const updatedPrices = [...addedPrices, selectedPrice]
        setAddedPrices(updatedPrices)
      }

      setAddedProductsList([
        ...addedProductsList,
        {
          id: String(productId),
          name: selectedProduct?.name,
          price: ((selectedPrice?.unit_amount || 0) / 100).toFixed(2),
          description: selectedPrice?.metadata?.internal_description,
          quantity: productQuantity,
          isAdjustableQuantity: allowQuantityAdjusting,
        } as Partial<StripeProduct>,
      ])
      setSavedLineItems([
        ...savedLineItems,
        {
          price: productPriceId,
          quantity: productQuantity,
          adjustable_quantity: {
            enabled: allowQuantityAdjusting && productQuantity < 999,
            minimum: allowQuantityAdjusting && productQuantity < 999 ? 1 : null,
            maximum:
              allowQuantityAdjusting && productQuantity < 999 ? 999 : null,
          },
        },
      ])
    } else {
      toast('Both Product and Price must be selected', { icon: '❌' })
    }
  }

  const handleRemoveProduct = (index: number) => {
    const updatedProducts = addedProductsList.filter((_, idx) => idx !== index)
    const updatedPrices = addedPrices.filter((_, idx) => idx !== index)

    setAddedProductsList(updatedProducts)
    setSavedLineItems(savedLineItems.filter((_, idx) => idx !== index))
    setAddedPrices(updatedPrices)

    // Reset to step 1 if no products remain
    if (updatedProducts.length === 0) {
      setCurrentStep(1)
      // Reset other fields
      setCouponId('')
      setHubspotDealLink('')
      setCollectShippingAddress(false)
    }
  }

  const handleSaveLineItems = () => {
    if (!hubspotDealLink) {
      toast.error('Hubspot Deal Link is required.')
      return
    }

    const basicUrlPattern = /^(https?:\/\/)?app\.hubspot\.com\//
    if (!basicUrlPattern.test(hubspotDealLink)) {
      toast.error('Link must be a valid app.hubspot.com URL.')
      return
    }

    const isSubscriptionLink = addedPrices.some(
      (price) => price.type === 'recurring'
    )
    const checkoutMode = isSubscriptionLink ? 'subscription' : 'payment'
    stripeCheckoutLink(
      {
        lineItems: savedLineItems,
        couponId: couponId,
        saleRepId: saleRepId,
        hasTrial: false,
        hubspotDealLink: hubspotDealLink,
        mode: checkoutMode,
        collectShippingAddress: collectShippingAddress,
        ...(!isSubscriptionLink && {
          stripeSuccessUrl: `${WEB_APP_URL}/stripe-non-subscription-success`,
        }),
      },
      {
        onSuccess: (data) => {
          setCheckoutLink(data.link)
        },
      }
    )
  }

  const handleStartOver = () => {
    setCouponId('')
    setSaleRepId(currentUser?.id)
    setAddedProductsList([])
    setSavedLineItems([])
    setCheckoutLink(undefined)
    setHubspotDealLink('')
    setCurrentStep(1)
    setAddedPrices([])
    setCollectShippingAddress(false)
  }

  const handleCouponSelect = (couponId: string) => {
    setCouponId(couponId)
    setShowCouponModal(false)
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active href="/non-subscription-purchase-link">
          Non Subscription Link Builder
        </Breadcrumb.Item>
      </Breadcrumb>
      <Card className="mt-4 mb-4 p-2 text-white bg-dark">
        <Card.Body>
          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-between">
                <h4 className="card-title">Non Subscription Link Builder</h4>
                {currentStep > 1 && (
                  <Button variant="outline-danger" onClick={handleStartOver}>
                    Start Over
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {/* Step 1: Only show Add Product button if no products exist */}
          {addedProductsList.length === 0 && (
            <Row className="mt-4">
              <Col lg={12}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setShowAddProductModal(true)}
                  >
                    + Add New Product
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          {/* Show added products table and Add New Product button below if products exist */}
          {addedProductsList.length > 0 && (
            <>
              <Row className="mt-4">
                <Col lg={12}>
                  <div className="form-floating mb-4">
                    <h4>Added Products</h4>
                    <Alert variant="secondary">
                      <StripeProductTable
                        addedProductsList={addedProductsList}
                        handleRemoveProduct={handleRemoveProduct}
                      />
                    </Alert>
                  </div>
                </Col>
              </Row>

              <Row className="mt-2 mb-4">
                <Col lg={12}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="primary"
                      onClick={() => setShowAddProductModal(true)}
                    >
                      + Add Another Product
                    </Button>
                  </div>
                </Col>
              </Row>

              {currentStep === 1 && (
                <Row>
                  <Col lg={12}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="success"
                        size="lg"
                        onClick={() => setCurrentStep(2)}
                      >
                        Next Step: Configure Link Options
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}

          {currentStep === 2 && (
            <>
              <hr />
              <div className="mb-3">
                <label htmlFor="coupon" className="form-label">
                  Coupon
                </label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    id="coupon"
                    type="text"
                    value={
                      availableCoupons?.find((c) => c.id === couponId)?.name ||
                      ''
                    }
                    placeholder="Select a coupon..."
                    readOnly
                    onClick={() => setShowCouponModal(true)}
                    style={{ cursor: 'pointer' }}
                  />
                  <Button
                    variant="primary"
                    onClick={() => setShowCouponModal(true)}
                    className="ms-2"
                  >
                    <ion-icon name="search" />
                  </Button>
                </div>
              </div>

              <HubspotDealLink
                hubspotDealLink={hubspotDealLink}
                setHubspotDealLink={setHubspotDealLink}
              />
              <StripeSelect
                id="saleReps"
                label="Sale Representative"
                labelDescription="Search or select a sale rep..."
                nullValue="Select sale rep..."
                value={saleRepId}
                setState={setSaleRepId}
                isFetching={isInternalAdminFetching}
                data={
                  internalAdmins?.map((admin) => ({
                    id: admin.id || '',
                    name: admin.name || admin.email || '',
                  })) || []
                }
                includeSearch={false}
              />

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="collectShippingAddress"
                  label="Collect Shipping Address"
                  checked={collectShippingAddress}
                  onChange={(e) => setCollectShippingAddress(e.target.checked)}
                />
              </Form.Group>

              <div className="d-flex mt-4 mb-4 w-full">
                <Button
                  className="mr-4 flex-grow-1"
                  onClick={handleSaveLineItems}
                  variant="success"
                  size="lg"
                  style={{
                    minWidth: '158px',
                    borderRadius: '4px 0px 0px 4px',
                  }}
                >
                  Generate Link
                </Button>

                <div
                  className="p-2 bg-white text-black d-flex justify-content-between align-items-center"
                  style={{
                    borderRadius: '0px 4px 4px 0px',
                    width: '100%',
                  }}
                >
                  {stripeCheckoutLinkLoading && <div>Loading...</div>}
                  {checkoutLink && (
                    <>
                      <div className="mb-1">{checkoutLink}</div>
                      <CopyToClipboardButton
                        copyText="Copy Link"
                        copiedText="Link Copied to Clipboard"
                        stringToCopy={checkoutLink}
                      />
                    </>
                  )}
                </div>
              </div>
              {currentStep > 1 && (
                <Row className="mt-4">
                  <Col lg={12}>
                    <Button variant="outline-danger" onClick={handleStartOver}>
                      Start Over
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <AddProductModal
        show={showAddProductModal}
        onHide={() => setShowAddProductModal(false)}
        handleAddProducts={handleAddProducts}
      />

      <SelectCouponModal
        show={showCouponModal}
        onHide={() => setShowCouponModal(false)}
        coupons={availableCoupons || []}
        onSelect={handleCouponSelect}
      />
    </Container>
  )
}

export default NonSubscriptionLinkBuilderPage
